class LightMotion {
  constructor() {
    this.animations = new Map();
    this.observer = null;
    this.init();
  }

  init() {
    // Initialize immediately and also on DOM content loaded
    this.setupIntersectionObserver();
    this.scanForAnimations();
    
    // Also set up a mutation observer to watch for dynamically added elements
    this.setupMutationObserver();
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.animateIn(entry.target);
          // Unobserve after animating to prevent re-animating
          this.observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1, // Start animation when 10% of the element is visible
      rootMargin: '0px 0px -50px 0px' // Adjust this to start animations a bit before they're in view
    });
  }

  setupMutationObserver() {
    // Watch for dynamically added elements
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.hasAttribute('scroll-fade')) {
              this.setupAnimation(node);
            } else {
              // Check child nodes for scroll-fade elements
              const elements = node.querySelectorAll ? 
                node.querySelectorAll('[scroll-fade]') : [];
              elements.forEach(el => this.setupAnimation(el));
            }
          }
        });
      });
    });

    // Start observing the document with the configured parameters
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  scanForAnimations() {
    // Find all elements with scroll-fade attribute
    const elements = document.querySelectorAll('[scroll-fade]');
    elements.forEach(element => {
      this.setupAnimation(element);
    });
  }

  setupAnimation(element) {
    // Skip if already set up
    if (element._lightmotionInitialized) return;
    
    // Mark as initialized
    element._lightmotionInitialized = true;
    
    // Parse animation configuration from attributes
    const config = {
      type: element.getAttribute('scroll-fade') || 'fade-up',
      delay: parseFloat(element.getAttribute('data-delay')) || 0,
      duration: parseFloat(element.getAttribute('data-duration')) || 0.6,
      distance: element.getAttribute('data-distance') || '20px',
      // Add more config options as needed
    };

    // Store the config
    this.animations.set(element, {
      config,
      isAnimated: false
    });

    // Set initial styles
    this.applyInitialStyles(element, config);

    // Start observing the element
    if (this.observer) {
      this.observer.observe(element);
    }
  }

  applyInitialStyles(element, config) {
    // Set initial styles based on animation type
    element.style.opacity = '0';
    element.style.transition = `all ${config.duration}s ease-out ${config.delay}s`;
    element.style.willChange = 'opacity, transform';
    
    // Set initial transform based on animation type
    const transform = this.getInitialTransform(config.type, config.distance);
    if (transform) {
      element.style.transform = transform;
    }
  }

  getInitialTransform(animationType, distance) {
    const transforms = {
      'fade-up': `translateY(${distance})`,
      'fade-down': `translateY(-${distance})`,
      'fade-left': `translateX(${distance})`,
      'fade-right': `translateX(-${distance})`,
      'fade': 'none',
      // Add more animation types as needed
    };
    
    return transforms[animationType] || transforms['fade-up'];
  }

  animateIn(element) {
    const animation = this.animations.get(element);
    if (!animation || animation.isAnimated) return;
    
    animation.isAnimated = true;
    
    // Apply final styles
    element.style.opacity = '1';
    element.style.transform = 'translate(0, 0)';
    
    // Clean up after animation completes
    const duration = (animation.config.duration + animation.config.delay) * 1000;
    setTimeout(() => {
      element.style.transition = 'none';
      element.style.willChange = 'auto';
    }, duration);
  }

  animateIn(element) {
    // Apply animation
    switch(element.config.type) {
      case 'fade':
        element.element.style.opacity = '1';
        break;
      // Add more animation types
    }
    element.isPlaying = true;
  }

  setupScrollAnimations() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const animation = this.animations.find(a => a.element === entry.target);
          if (animation && !animation.isPlaying) {
            this.animateIn(animation);
          }
        }
      });
    }, {
      threshold: 0.1
    });

    this.animations.forEach(anim => {
      observer.observe(anim.element);
    });
  }
}

// Auto-initialize
if (typeof window !== 'undefined') {
  window.LightMotion = new LightMotion();
  
  // Also expose a global function for manual initialization if needed
  window.initLightMotion = function() {
    return new LightMotion();
  };
}
